import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Box,
  TextField,
  Pagination,
} from "@mui/material";
import axios from "axios";
import API_BASE_URL from "../config";
import { UserContext } from "../context/UserContext"; // Adjust the import if needed

const PaymentHistory = () => {
  const { user } = useContext(UserContext);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // Number of items per page
  const [total, setTotal] = useState(0);

  const fetchPayments = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/payments`);

      const userId = user.id;

      // ✅ First filter by logged-in user
      let filtered = response.data.payments.filter(
        (payment) => payment.user_id === userId
      );

      // ✅ Then apply search locally
      if (search.trim()) {
        const searchLower = search.toLowerCase();
        filtered = filtered.filter(
          (p) =>
            p.name?.toLowerCase().includes(searchLower) ||
            p.opportunity?.toLowerCase().includes(searchLower) ||
            p.transaction_id?.toLowerCase().includes(searchLower)
        );
      }

      setPayments(filtered);
      setTotal(filtered.length);
    } catch (error) {
      console.error("Error fetching payment history:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPayments();
  }, [search, user]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 4 }}>
      <Typography
        variant="h4"
        sx={{ color: "#42326e" }}
        fontWeight="bold"
        textAlign="center"
        mb={3}
      >
        Payment History
      </Typography>

      <Box display="flex" justifyContent="space-between" mb={3}>
        <TextField
          label="Search"
          variant="outlined"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1); // Reset page when searching
          }}
          sx={{ width: "30%" }}
        />
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                {[
                  "S.No",
                  "Name",
                  "Opportunity",
                  "Amount",
                  "Start Date",
                  "End Date",
                  "Status",
                  "Payment Method",
                  "Transaction ID",
                ].map((header, idx) => (
                  <TableCell
                    key={idx}
                    sx={{
                      color: "#42326e",
                      fontSize: "1rem",
                      fontWeight: "bold",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {payments
                .slice((page - 1) * limit, page * limit)
                .map((payment, index) => (
                  <TableRow key={payment.id}>
                    <TableCell>{index + 1 + (page - 1) * limit}</TableCell>
                    <TableCell>{payment.name}</TableCell>
                    <TableCell>{payment.opportunity}</TableCell>
                    <TableCell>{payment.amount}</TableCell>
                    <TableCell>{new Date(payment.start_date).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(payment.end_date).toLocaleDateString()}</TableCell>
                    <TableCell>{payment.status}</TableCell>
                    <TableCell>{payment.payment_method}</TableCell>
                    <TableCell>{payment.transaction_id}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={Math.ceil(total / limit)}
              page={page}
              onChange={handlePageChange}
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#42326e",
                  borderColor: "#42326e",
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: "#42326e",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#352959",
                  },
                },
              }}
            />
          </Box>
        </>
      )}
    </Container>
  );
};

export default PaymentHistory;
